import { dispatchCustomEvent } from './ui-helpers.js';

/**
 * @alias SwipeObserver
 * @classdesc Class for handling swipe gestures.
 * @example
 * const observer = new SwipeObserver(document.body);
 * document.body.addEventListener('swipe-left', (e) => console.log('Swiped left!', e));
 * document.body.addEventListener('swipe-right', (e) => console.log('Swiped right!', e));
 * document.body.addEventListener('swipe-bottom', (e) => console.log('Swiped bottom!', e));
 * document.body.addEventListener('swipe-top', (e) => console.log('Swiped top!', e));
 * observer.disconnect();
 */
export class SwipeObserver {
    /**
     * @param {HTMLElement} element
     * @param {number} [threshold]
     * @property {HTMLElement} element Element to observe
     * @property {number} threshold Threshold in pixels
     */
    constructor(element, threshold = 10) {
        if (!(element instanceof HTMLElement)) {
            throw new TypeError('Element has to be instance of HTMLElement');
        }
        this.element = element;
        this.threshold = threshold;

        this.swipeStartTime = -1;
        this.swipeStartX = 0;
        this.swipeStartY = 0;

        this.handleSwipeStart = this.handleSwipeStart.bind(this);
        this.handleSwipeEnd = this.handleSwipeEnd.bind(this);
        this.subscribeSwipeEvents();
    }

    /**
     * @type {number}
     * @private
     */
    static get SwipeSpeedThreshold() {
        return 0.5; // pixels per millisecond
    }

    /**
     * Destroy all listeners
     */
    disconnect() {
        this.unsubscribeSwipeEvents();
    }

    /**
     * @private
     */
    subscribeSwipeEvents() {
        this.element.addEventListener('touchstart', this.handleSwipeStart, {
            capture: false,
            passive: true,
        });
        this.element.addEventListener('touchend', this.handleSwipeEnd, false);
        this.element.addEventListener(
            'pointerdown',
            this.handleSwipeStart,
            false
        );
        this.element.addEventListener('pointerup', this.handleSwipeEnd, false);
    }

    /**
     * @private
     */
    unsubscribeSwipeEvents() {
        this.element.removeEventListener(
            'touchstart',
            this.handleSwipeStart,
            false
        );
        this.element.removeEventListener(
            'touchend',
            this.handleSwipeEnd,
            false
        );
        this.element.removeEventListener(
            'pointerdown',
            this.handleSwipeStart,
            false
        );
        this.element.removeEventListener(
            'pointerup',
            this.handleSwipeEnd,
            false
        );
    }

    /**
     * @param {PointerEvent | TouchEvent} event
     * @private
     */
    handleSwipeStart(event) {
        this.swipeStartX = event.type.includes('touch')
            ? event.touches[0].clientX
            : event.clientX;
        this.swipeStartY = event.type.includes('touch')
            ? event.touches[0].clientY
            : event.clientY;
        this.swipeStartTime = Date.now();
    }

    /**
     * @param {PointerEvent | TouchEvent} event
     * @private
     */
    handleSwipeEnd(event) {
        const swipeEndX = event.type.includes('touch')
            ? event.changedTouches[0].clientX
            : event.clientX;
        const swipeEndY = event.type.includes('touch')
            ? event.changedTouches[0].clientY
            : event.clientY;
        const swipeXDelta = swipeEndX - this.swipeStartX;
        const swipeYDelta = swipeEndY - this.swipeStartY;
        const endTime = Date.now();
        const deltaTime = endTime - this.swipeStartTime;
        const speed = swipeYDelta / deltaTime;
        const details = {
            swipeStartX: this.swipeStartX,
            swipeStartY: this.swipeStartY,
            swipeEndX: swipeEndX,
            swipeEndY: swipeEndY,
            swipeXDelta,
            swipeYDelta,
            originalEvent: event,
        };

        // Swipe is not fast enough, this guards actions from slow swiping.
        if (speed < SwipeObserver.SwipeSpeedThreshold) {
            return;
        }

        if (Math.abs(swipeXDelta) > Math.abs(swipeYDelta)) {
            if (swipeXDelta > 0) {
                dispatchCustomEvent(this.element, 'swipe-right', details);
            } else {
                dispatchCustomEvent(this.element, 'swipe-left', details);
            }

            return;
        }

        if (swipeYDelta > 0) {
            dispatchCustomEvent(this.element, 'swipe-bottom', details);
            return;
        }

        dispatchCustomEvent(this.element, 'swipe-top', details);
    }
}
